import {
  AdditionalInformation,
  DestinationCatalog,
  PaginatedCatalogData,
  PaginatedCatalogResponseData,
  ProductCatalogRequest,
  ProductCatalogResponse
} from '@/types/DestinationCatalog'
import BaseService from './baseService'
import EndPoints from '@/constants/dxApiEndpoints'
import { PatchRequest } from '@/types/patchRequest'
import lodash from 'lodash'
import { sampleConfigJson } from '@/constants/destinationCatalogs'
import { ProductFeedResponse } from '@/types/productFeed'
import { AttributeValue, CustomAttributeValue } from '@/types/attributeValue'
import { LinkedCatalogResponse } from '@/constants/catalogManager'

class CatalogService extends BaseService {
  CreateCatalog = async (
    businessProfileId: number,
    requestData: DestinationCatalog
  ): Promise<number> => {
    const response = await this.apiClient.post<{ id: number }>(
      EndPoints.CreateCatalog(businessProfileId),
      { ...requestData, isRoimatic: true }
    )
    return response.id
  }

  UpdateCatalog = async (
    businessProfileId: number,
    id: number,
    patchRequest: PatchRequest[]
  ) => {
    try {
      await this.apiClient.patch(
        EndPoints.UpdateCatalog(businessProfileId, id),
        { requestData: patchRequest, isRoimatic: true }
      )
    } catch (error: unknown) {
      throw new Error('Failed to update catalog')
    }
  }

  GetCatalogById = async (businessProfileId: number, catalogId: number) => {
    if (!catalogId) {
      return null
    }

    const catalogResponse: {
      data: ProductCatalogResponse
    } = await this.apiClient.get(
      EndPoints.GetCatalogById(businessProfileId, catalogId)
    )

    if (!catalogResponse?.data) {
      return null
    }

    const feedResponse: { data: ProductFeedResponse[] } =
      await this.apiClient.get(EndPoints.GetProductFeeds(businessProfileId), {
        catalogIds: catalogId.toString(),
        feedType: 1
      })

    const catalogData: DestinationCatalog = {
      id: catalogResponse.data.id,
      name: catalogResponse.data.catalogName,
      catalogTypeId: catalogResponse.data.catalogTypeId,
      status: catalogResponse.data.status,
      channels: catalogResponse.data.channels,
      labels: catalogResponse.data.labels,
      configJson: feedResponse.data[0]?.configJson ?? sampleConfigJson,
      productCount: catalogResponse.data.productCount,
      createdAt: catalogResponse.data.createdAt,
      searchEngineProductCatalogId:
        catalogResponse.data.searchEngineProductCatalogId,
      sourceFeedId: feedResponse.data[0]?.id
    }

    return catalogData
  }

  getPaginatedCatalogs = async (
    businessProfileId: number | undefined,
    productCatalogRequest: ProductCatalogRequest
  ): Promise<{ data: PaginatedCatalogResponseData } | null> => {
    if (!productCatalogRequest) {
      return null
    }

    const catalogResponse: {
      data: PaginatedCatalogResponseData
    } = await this.apiClient.get(
      EndPoints.GetPaginatedCatalogs(businessProfileId || 0),
      productCatalogRequest
    )

    if (!catalogResponse?.data?.data?.length) {
      return null
    }

    return catalogResponse
  }

  generatePatchRequests(
    oldValue: DestinationCatalog,
    updatedValue: DestinationCatalog
  ): PatchRequest[] {
    return (Object.keys(updatedValue) as (keyof DestinationCatalog)[]).reduce(
      (acc: PatchRequest[], key) => {
        if (
          !lodash.isEqual(oldValue[key], updatedValue[key]) &&
          updatedValue[key]
        ) {
          let value: string = ''

          if (
            typeof updatedValue[key] === 'object' &&
            updatedValue[key] !== null
          ) {
            value = JSON.stringify(updatedValue[key])
          } else {
            value = String(updatedValue[key])
          }

          acc.push({ name: key, value: value })
        }
        return acc
      },
      []
    )
  }

  getAttributeValues = async (
    productParameterIds: string,
    businessProfileId: number,
    catalogId: number
  ): Promise<AttributeValue[]> => {
    const url: string = EndPoints.GetAttributeValues(
      businessProfileId,
      catalogId
    )

    const response = await this.apiClient.get<{
      data: AttributeValue[]
    }>(url, { productParameterIds })

    const productParameters: AttributeValue[] = response?.data
    return productParameters
  }

  getCustomAttributeValues = async (
    businessProfileId: number,
    catalogId: number,
    productIds: string,
    parameterIds: string | null,
    computedColumnMappings: string
  ): Promise<CustomAttributeValue[]> => {
    const url: string = EndPoints.getCustomAttributeValues(
      businessProfileId,
      catalogId
    )

    const response = await this.apiClient.get<{
      data: CustomAttributeValue[]
    }>(url, {
      productIds: productIds,
      parameterIds: parameterIds,
      computedColumnMappings: computedColumnMappings
    })

    return response?.data
  }

  syncCatalog = async (businessProfileId: number, catalogId: number) => {
    await this.apiClient.post(
      EndPoints.SyncCatalog(businessProfileId, catalogId),
      {}
    )
  }

  getValidAttributeValuesCount = async (
    businessProfileId: number,
    productCatalogId: number,
    productParameterId: number,
    attributeValues: string[]
  ): Promise<number> => {
    const url: string = EndPoints.GetValidAttributeValuesCount(
      businessProfileId,
      productCatalogId,
      productParameterId
    )
    const response = await this.apiClient.post<{ data: number }>(url, {
      attributeValues
    })
    return response?.data
  }

  getLinkedCatalogs = async (
    businessProfileId: number,
    engineCatalogIds: string
  ): Promise<LinkedCatalogResponse[]> => {
    const response = await this.apiClient.get(
      EndPoints.GetLinkedCatalogs(businessProfileId),
      {
        engineCatalogIds
      }
    )

    return response
  }

  processCatalog = async (
    businessProfileId: number,
    catalogId: number,
    triggerSync: boolean = false
  ) => {
    await this.apiClient.post(
      EndPoints.ProcessCatalog(businessProfileId, catalogId),
      null,
      { params: { triggerSync } }
    )
  }
}

export default CatalogService
